import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { homeFor } from "../lib/home";
import type { CurrentUser } from "../lib/types";
import { Button, ErrorNote, Field, Input } from "../components/ui";
import { AuthFrame, CompactWordmark } from "../components/Brand";

type LoginResponse =
  | { status: "totp_required"; challenge: string }
  | { status: "signed_in"; user: CurrentUser };

type Step = { name: "password" } | { name: "code"; challenge: string };

export function SignIn() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>({ name: "password" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (loading) return <Splash />;
  if (user !== null) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? homeFor(user.role)} replace />;
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);

    try {
      const result = await api<LoginResponse>("/api/v1/auth/login", {
        method: "POST",
        body: { email, password },
      });

      setPassword("");

      // Only accounts that have turned two-factor on see a second step.
      if (result.status === "signed_in") {
        setUser(result.user);
        navigate(homeFor(result.user.role), { replace: true });
        return;
      }

      setStep({ name: "code", challenge: result.challenge });
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setPending(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (step.name === "password") return;

    setError(null);
    setPending(true);

    try {
      const result = await api<{ user: CurrentUser }>("/api/v1/auth/totp", {
        method: "POST",
        body: { challenge: step.challenge, code },
      });
      setUser(result.user);
      navigate(homeFor(result.user.role), { replace: true });
    } catch (caught) {
      setError(messageFor(caught));
      setCode("");
      // An expired challenge cannot be retried — the password step has to run
      // again, so send the user back rather than leaving them on a dead form.
      if (caught instanceof ApiError && caught.code === "INVALID_CHALLENGE") {
        setStep({ name: "password" });
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthFrame>
      <header className="space-y-2">
        <CompactWordmark />
        <h1 className="display pt-4 text-[2.5rem] text-ink lg:pt-0">Sign in</h1>
        <p className="text-sm text-muted">
          {step.name === "password"
            ? "Use the account we set up for you."
            : "One more step, from your phone."}
        </p>
      </header>

      {step.name === "password" ? (
        <form onSubmit={submitPassword} className="mt-8 space-y-4">
          <Field label="Email">
            <Input
              type="email"
              value={email}
              autoComplete="username"
              required
              autoFocus
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <div className="space-y-1.5">
            <Field label="Password">
              <Input
                type="password"
                value={password}
                autoComplete="current-password"
                required
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            {/* Under the field it applies to, where somebody who has just
                mistyped it twice is already looking. */}
            <Link
              to="/forgot-password"
              className="inline-block text-xs text-dim underline-offset-4 transition-colors hover:text-muted hover:underline"
            >
              Forgotten your password?
            </Link>
          </div>

          {error !== null && <ErrorNote>{error}</ErrorNote>}

          {/* Taller than a table button: this is the only control on the
              screen and it is pressed with a thumb. */}
          <Button
            type="submit"
            variant="primary"
            className="min-h-[2.75rem] w-full"
            disabled={pending}
          >
            {pending ? "Checking…" : "Continue"}
          </Button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="mt-8 space-y-4">
          <Field label="Six digit code" hint="From your authenticator app.">
            <Input
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              autoFocus
              className="tabular text-lg tracking-[0.4em]"
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            />
          </Field>

          {error !== null && <ErrorNote>{error}</ErrorNote>}

          <Button
            type="submit"
            variant="primary"
            className="min-h-[2.75rem] w-full"
            disabled={pending || code.length !== 6}
          >
            {pending ? "Verifying…" : "Sign in"}
          </Button>
        </form>
      )}

      {/* Only on the first step. Somebody halfway through a code has an
          account already, and offering them a new one is noise. */}
      {step.name === "password" && (
        <p className="mt-10 border-t border-hairline pt-6 text-sm text-dim">
          No account yet?{" "}
          <Link
            to="/register"
            className="text-muted underline underline-offset-4 transition-colors hover:text-ink"
          >
            Create one
          </Link>{" "}
          — it is free, and you can order from inside it.
        </p>
      )}
    </AuthFrame>
  );
}

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-sm text-dim">Loading…</div>
  );
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "Something went wrong. Try again.";
}
